import { useId } from "react";

/**
 * The Satatam eye-mark — identical geometry + animation to the
 * marketing site's AnimatedLogo (static/landing/sections.jsx + styles.css).
 * Mint sphere, lower-hemisphere circuit traces, held-breath dark iris with
 * twin catchlights, an eyelid that blinks. Class names match the marketing
 * site (.logo-mark / .logo-iris / .logo-lid / .logo-highlight) so the exact
 * same keyframes in base.css drive it (logoLook drift + logoBlink + twinkle),
 * reduced-motion aware. Gradient ids are uniquified per instance.
 *
 * One deliberate difference from the marketing SVG: no halo. That mark draws a
 * r=118 gradient circle around its r=78 sphere, and the marketing page adds a
 * `drop-shadow` on top; both are wanted there, on a page whose background is
 * lit. In the console the mark is a sidebar logo, and the WebGL orb it stands
 * in for casts nothing at all -- so a fallback that glowed read as a different
 * logo from the live one, and different again between the two themes, because
 * every glow token is theme-dependent.
 */
export function BrandMark({ size = 28, className }: { size?: number; className?: string }) {
  const uid = useId().replace(/:/g, "");
  return (
    <svg
      className={className ? `logo-mark is-animated ${className}` : "logo-mark is-animated"}
      width={size}
      height={size}
      /* Cropped to the sphere (cx=cy=120, r=78) with a 2u margin for the
         antialiased rim, so the mark fills its slot the way the live WebGL
         orb does. Every coordinate below is unchanged: this only rescales
         user units to pixels. */
      viewBox="40 40 160 160"
      role="img"
      aria-label="Satatam"
    >
      <defs>
        <radialGradient id={`bm-sphere-${uid}`} cx="38%" cy="32%" r="78%">
          <stop offset="0" stopColor="#E8FFF1" />
          <stop offset="0.25" stopColor="#A6F2CC" />
          <stop offset="0.55" stopColor="#5EDCA0" />
          <stop offset="0.85" stopColor="#2A9F70" />
          <stop offset="1" stopColor="#114F38" />
        </radialGradient>
        <radialGradient id={`bm-depth-${uid}`} cx="50%" cy="100%" r="70%">
          <stop offset="0" stopColor="#000" stopOpacity="0.35" />
          <stop offset="0.55" stopColor="#000" stopOpacity="0.05" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`bm-sheen-${uid}`} cx="34%" cy="24%" r="32%">
          <stop offset="0" stopColor="#FFF" stopOpacity="0.78" />
          <stop offset="0.55" stopColor="#FFF" stopOpacity="0.18" />
          <stop offset="1" stopColor="#FFF" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`bm-pupil-${uid}`} cx="42%" cy="38%" r="70%">
          <stop offset="0" stopColor="#0A1B14" />
          <stop offset="0.75" stopColor="#01060A" />
          <stop offset="1" stopColor="#062014" />
        </radialGradient>
        <radialGradient id={`bm-rim-${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0.9" stopColor="#000" stopOpacity="0" />
          <stop offset="0.97" stopColor="#03100A" stopOpacity="0.45" />
          <stop offset="1" stopColor="#03100A" stopOpacity="0.6" />
        </radialGradient>
        <filter id={`bm-bloom-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.5" />
        </filter>
        <clipPath id={`bm-clip-${uid}`}>
          <circle cx="120" cy="120" r="78" />
        </clipPath>
      </defs>

      <g clipPath={`url(#bm-clip-${uid})`}>
        <circle cx="120" cy="120" r="78" fill={`url(#bm-sphere-${uid})`} />
        {/* Traces are drawn twice: a blurred pass for the glow the etched
            lines throw into the sphere, then the crisp pass on top. Dropping
            the blurred pass is what made this port read flatter than the same
            mark on the marketing site and in the widget. */}
        <g stroke="#C8FFE3" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <g opacity="0.65" filter={`url(#bm-bloom-${uid})`} strokeWidth="2.2">
            <path d="M62 122 L84 122 L92 130 L108 130" />
            <path d="M58 138 L80 138 L88 146 L102 146 L102 156" />
            <path d="M70 158 L94 158 L100 164 L116 164" />
            <path d="M132 158 L156 158 L162 152 L172 152" />
            <path d="M138 142 L156 142 L162 136 L176 136" />
            <path d="M148 124 L168 124" />
            <path d="M104 174 L120 174 L126 180 L138 180" />
          </g>
          <g opacity="0.95" strokeWidth="1.1">
            <path d="M62 122 L84 122 L92 130 L108 130" />
            <path d="M58 138 L80 138 L88 146 L102 146 L102 156" />
            <path d="M70 158 L94 158 L100 164 L116 164" />
            <path d="M132 158 L156 158 L162 152 L172 152" />
            <path d="M138 142 L156 142 L162 136 L176 136" />
            <path d="M148 124 L168 124" />
            <path d="M104 174 L120 174 L126 180 L138 180" />
            <path d="M92 168 L92 178" />
            <path d="M150 168 L150 178" />
          </g>
          <g fill="#D8FFEC" stroke="none">
            <circle cx="62" cy="122" r="1.6" /><circle cx="58" cy="138" r="1.6" />
            <circle cx="70" cy="158" r="1.6" /><circle cx="102" cy="156" r="1.4" />
            <circle cx="172" cy="152" r="1.6" /><circle cx="176" cy="136" r="1.6" />
            <circle cx="168" cy="124" r="1.4" /><circle cx="138" cy="180" r="1.6" />
            <circle cx="92" cy="178" r="1.4" /><circle cx="150" cy="178" r="1.4" />
          </g>
        </g>
        <circle cx="120" cy="120" r="78" fill={`url(#bm-depth-${uid})`} />
        <ellipse cx="92" cy="74" rx="40" ry="26" fill={`url(#bm-sheen-${uid})`} />
        {/* Rim catchlight: the sphere's right edge picking up the key light. */}
        <ellipse cx="178" cy="118" rx="6" ry="14" fill="#FFFFFF" opacity="0.18" />
      </g>

      {/* Iris group — drifts (logoLook). Lid is a child so it stays aligned. */}
      <g className="logo-iris">
        <circle cx="140" cy="100" r="22" fill={`url(#bm-pupil-${uid})`} />
        <circle className="logo-highlight" cx="148" cy="92" r="5.2" fill="#E5FFEF" />
        <circle cx="148" cy="92" r="3" fill="#FFFFFF" />
        <circle cx="132" cy="111" r="1.8" fill="#7CFFB6" opacity="0.55" />
        {/* Eyelid: a rect (clipped to the iris circle) that scales down from the
            top to blink. Filled with the sphere gradient so it reads as lid. */}
        <rect className="logo-lid" x="118" y="78" width="44" height="44" fill={`url(#bm-sphere-${uid})`} />
      </g>

      <circle cx="120" cy="120" r="78" fill={`url(#bm-rim-${uid})`} />
    </svg>
  );
}
